import React, { useContext, useState } from 'react'

import { useI18n } from '@shopify/react-i18n'
import { ButtonV2, Column, Row, Sheet, Text } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'

import filterContext from '@/components/pages/production/components/context'
import { FilterFields, TypeFilterState } from '@/components/pages/production/components/context/types'
import { DEFAULT_CURRENCY_SYMBOL } from '@/context/internationalization/constants'
import ListingsContext from '@/context/listings'
import { getHighestFilterPrice } from '@/utils/filters'
import { getFormattedCurrency } from '@/utils/i18n'

import FiltersPrice from '../../filters-price'
import FiltersQuantity from '../../filters-quantity'
import FiltersTypes from '../../filters-types'
import GroupFilterChipRow from '../../group-chips/component'
import SortByButtons from '../filters-desktop-sort-by-buttons'

import style from './styles.module.scss'

const DesktopFilters: React.FC = () => {
    const [showSortByDropDown, setShowSortByDropDown] = useState(false)
    const [showQuantityDropDown, setShowQuantityDropDown] = useState(false)
    const [showPriceDropDown, setShowPriceDropDown] = useState(false)
    const [showTypeDropDown, setShowTypeDropDown] = useState(false)
    const {
        state: filterState,
        selectors: { getCurrentSortByButtonTextSelector },
        dispatch: {
            onPriceFilterChangeAction: onPriceFilterChange,
            onFilterResetExceptSectionsAction: onFilterResetExceptSections,
        },
    } = useContext(filterContext)
    const { selectors: listingsSelectors } = useContext(ListingsContext)
    const { globalPriceRangeObject: priceRangeObject } = listingsSelectors
    const { priceFilterState, showAllInPrice } = filterState
    const { currentMin, currentMax } = priceFilterState
    const [i18n] = useI18n()

    const highestFilterPrice = getHighestFilterPrice(priceRangeObject, showAllInPrice)
    const highestGlobalPrice = showAllInPrice ? priceRangeObject.highestAllInPrice : priceRangeObject.highestPrice
    const priceMaxed = highestFilterPrice === currentMax

    const showHideFilters = (filterType: 'sort' | 'price' | 'quantity' | 'type') => {
        switch (filterType) {
            case 'sort':
                setShowQuantityDropDown(false)
                setShowPriceDropDown(false)
                setShowTypeDropDown(false)
                setShowSortByDropDown(!showSortByDropDown)
                break
            case 'quantity':
                setShowSortByDropDown(false)
                setShowPriceDropDown(false)
                setShowTypeDropDown(false)
                setShowQuantityDropDown(!showQuantityDropDown)
                break
            case 'price':
                setShowSortByDropDown(false)
                setShowQuantityDropDown(false)
                setShowTypeDropDown(false)
                setShowPriceDropDown(!showPriceDropDown)
                break
            default:
                setShowSortByDropDown(false)
                setShowQuantityDropDown(false)
                setShowPriceDropDown(false)
                setShowTypeDropDown(!showTypeDropDown)
        }
    }

    const getTypeButtonText = (typeFilterState: TypeFilterState) => {
        const filterTypesLength = Object.values(typeFilterState).filter((el) => el === true).length
        switch (filterTypesLength) {
            case 0:
                return 'Any Type'
            case 1:
                return '1 Type'
            default:
                return `${filterTypesLength} Types`
        }
    }

    const getQuantityButtonText = (quantity: number) => {
        if (quantity == 0) {
            return 'Any Quantity'
        } else if (quantity == 1) {
            return '1 Ticket'
        } else if (quantity > 1 && quantity < 8) {
            return `${quantity} Tickets`
        } else {
            return '8+ Tickets'
        }
    }

    const getPriceButtonText = () => {
        /**
         * Positive and negative infinity are used as special values here to represent when the user has cleared
         * out either the min or max price input. See `price-slider` component and the `filteredListingsSelectorMap` in
         * the `filterContext` for more details.
         */
        const i18nListingsData = listingsSelectors?.i18nListingsData
        const currencySymbol = i18n.getCurrencySymbol(i18nListingsData?.to).symbol || DEFAULT_CURRENCY_SYMBOL

        const currentMinStr: string =
            currentMin > 0 && currentMin !== Infinity
                ? `${getFormattedCurrency(
                      i18n,
                      currentMin,
                      i18nListingsData?.to,
                      i18nListingsData?.applyCurrencyConversion,
                      0,
                  )}`
                : `${currencySymbol}0`
        const currentMaxStr: string =
            currentMax > 0 && currentMax !== -Infinity
                ? `${getFormattedCurrency(
                      i18n,
                      currentMax,
                      i18nListingsData?.to,
                      i18nListingsData?.applyCurrencyConversion,
                      0,
                  )}`
                : `${currencySymbol}0`
        return `${currentMinStr} - ${currentMaxStr}`
    }

    // Reset display text if the inputs are blank and close the filter.
    const handlePriceMouseLeave = () => {
        const newMin =
            currentMin === Number.POSITIVE_INFINITY ||
            currentMin > highestFilterPrice ||
            currentMin < priceRangeObject.lowestPrice
                ? priceRangeObject.lowestPrice
                : currentMin

        let newMax =
            currentMax === Number.NEGATIVE_INFINITY || currentMax > highestFilterPrice ? highestFilterPrice : currentMax

        newMax = newMax < newMin ? newMin : newMax

        newMin != currentMin &&
            onPriceFilterChange({ field: 'currentMin', value: newMin, shouldTrackFilterChange: false })
        newMax != currentMax &&
            onPriceFilterChange({ field: 'currentMax', value: newMax, shouldTrackFilterChange: false })
        showHideFilters('price')
    }

    return (
        <div className={style.filterContainer}>
            <Row className={style.filterRow}>
                <Row onMouseLeave={() => showHideFilters('sort')}>
                    <ButtonV2
                        onMouseEnter={() => {
                            if (!showSortByDropDown) {
                                showHideFilters('sort')
                            }
                        }}
                        className={classNames(style.filterButtons, {
                            [style.filterButtonSelected]: showSortByDropDown,
                            [style.isChanged]: filterState.modifiedFilterList.includes(FilterFields.Type),
                        })}
                        data-testid="show-sort-filter-button"
                    >
                        {getCurrentSortByButtonTextSelector}
                    </ButtonV2>
                    <Column mt={4.25} ml={1} className={`${style.dropdown} ${style.sortDropdown}`}>
                        <Sheet
                            toggle={showSortByDropDown}
                            shadowSize="lg"
                            enterFrom="top"
                            timeoutLength={0}
                            position="relative"
                            className={style.sortDropdownSheet}
                            transition={false}
                        >
                            <SortByButtons />
                        </Sheet>
                    </Column>
                </Row>
                <Row onMouseLeave={() => showHideFilters('quantity')}>
                    <ButtonV2
                        onMouseEnter={() => {
                            if (!showQuantityDropDown) {
                                showHideFilters('quantity')
                            }
                        }}
                        className={classNames(style.filterButtons, {
                            [style.filterButtonSelected]: showQuantityDropDown,
                            [style.isChanged]: filterState.modifiedFilterList.includes(FilterFields.Quantity),
                        })}
                        data-testid="show-quantity-filter-button"
                    >
                        {getQuantityButtonText(filterState.quantityFilterState.quantity)}
                    </ButtonV2>
                    <Column mt={4.25} ml={1} className={`${style.dropdown} ${style.quantityDropdown}`}>
                        <Sheet
                            toggle={showQuantityDropDown}
                            shadowSize="lg"
                            enterFrom="top"
                            timeoutLength={0}
                            position="relative"
                            className={style.quantityDropdownSheet}
                            transition={false}
                        >
                            <FiltersQuantity />
                        </Sheet>
                    </Column>
                </Row>
                <Row onMouseLeave={() => handlePriceMouseLeave()}>
                    <ButtonV2
                        onMouseEnter={() => {
                            if (!showPriceDropDown) {
                                showHideFilters('price')
                            }
                        }}
                        className={classNames(style.filterButtons, {
                            [style.filterButtonSelected]: showPriceDropDown,
                            [style.isChanged]:
                                filterState.modifiedFilterList.includes(FilterFields.PriceRange) ||
                                filterState.modifiedFilterList.includes(FilterFields.AllInPrice),
                        })}
                        data-testid="show-price-filter-button"
                    >
                        {getPriceButtonText()}
                        {priceMaxed && highestFilterPrice < highestGlobalPrice ? '+' : ''}
                    </ButtonV2>
                    <Column mt={4.25} ml={1} className={`${style.dropdown} ${style.priceDropdown}`}>
                        <Sheet
                            toggle={showPriceDropDown}
                            shadowSize="lg"
                            enterFrom="top"
                            timeoutLength={0}
                            position="relative"
                            className={style.priceDropdownSheet}
                            transition={false}
                        >
                            <FiltersPrice />
                        </Sheet>
                    </Column>
                </Row>
                <Row onMouseLeave={() => showHideFilters('type')}>
                    <ButtonV2
                        onMouseEnter={() => {
                            if (!showTypeDropDown) {
                                showHideFilters('type')
                            }
                        }}
                        className={classNames(style.filterButtons, {
                            [style.filterButtonSelected]: showTypeDropDown,
                            [style.isChanged]: filterState.modifiedFilterList.includes(FilterFields.TicketType),
                        })}
                        data-testid="show-type-filter-button"
                    >
                        {getTypeButtonText(filterState.typeFilterState)}
                    </ButtonV2>
                    <Column mt={4.25} ml={1} className={`${style.dropdown} ${style.typeDropdown}`}>
                        <Sheet
                            toggle={showTypeDropDown}
                            shadowSize="lg"
                            enterFrom="top"
                            timeoutLength={0}
                            position="relative"
                            className={style.typeDropdownSheet}
                            transition={false}
                        >
                            <FiltersTypes />
                        </Sheet>
                    </Column>
                </Row>
                {filterState.modifiedFilterList.length > 0 && (
                    <Row>
                        <ButtonV2
                            importance="tertiary"
                            data-testid={'mobile-filters-reset-button'}
                            className={style.filterButtons}
                            onClick={() => {
                                onFilterResetExceptSections()
                            }}
                        >
                            <Text
                                as="span"
                                weight="regular-bold"
                                size="lg"
                                className={style.clearAllFiltersText}
                                altFont
                            >
                                Clear filters
                            </Text>
                        </ButtonV2>
                    </Row>
                )}
            </Row>
            <GroupFilterChipRow showDivider />
        </div>
    )
}

export default DesktopFilters
